/**
 * Column 3D generation (STRUCTURE round, 2026-08 — docs/bim-plan/08 Part 2): a plain
 * vertical PRISM on the standard extrusion path — the profile ring (kernel
 * columnProfileRing: rect or 20-segment round) extruded over the resolved z range.
 * Being a prism, every consumer derives from it with zero category code: 3D extrusion
 * + prismEdges, section/elevation cut rects (section.ts prism pass), vertical
 * pick/snap (verticalPick's generic ring crossings), spot elevations, cutaway.
 * The PLAN story lives in plan.ts's structural pass (cut profile with material poché
 * when the plane passes through; solid below / dashed above otherwise) — hence
 * `skipProjectedOutline`.
 *
 * Z RANGE — the levels-as-datums contract: base = element level + baseOffsetFt; top =
 * unconnected height, OR the referenced top level (el.paramLevels.topLevelId,
 * materialized by resolveElement) + topOffsetFt. topMode 'level' with no level chosen
 * yet falls back to the unconnected height so a half-configured column never vanishes.
 */
import type { ResolvedElement } from '@aj/bim-kernel'
import { columnProfileRing, tintMaterialId } from '@aj/bim-kernel'
import type { ElementSolid } from '../wall/generate3d'

/** the column's WORLD vertical extent, or null when degenerate/unplaced */
export function columnZRange(el: ResolvedElement): { z0: number; z1: number } | null {
  if (!el.level) return null
  const z0 = el.level.elevationFt + ((el.params.baseOffsetFt as number) || 0)
  const topLevel = el.params.topMode === 'level' ? el.paramLevels?.topLevelId : undefined
  const z1 = topLevel
    ? topLevel.elevationFt + ((el.params.topOffsetFt as number) || 0)
    : z0 + ((el.params.unconnectedHeightFt as number) || 8)
  return z1 - z0 > 1e-6 ? { z0, z1 } : null
}

/** COLORS: the brushed / standard color over the member (3D only — ElementSolid.surfaceMaterialId) */
const paintOf = (el: ResolvedElement): { surfaceMaterialId?: string } => {
  const mat = (typeof el.params.material === 'string' && el.params.material) || 'default'
  return el.memberColor ? { surfaceMaterialId: tintMaterialId(el.memberColor, mat) } : {}
}

export function columnSolid(el: ResolvedElement): ElementSolid | null {
  const ring = columnProfileRing(el)
  const zr = columnZRange(el)
  if (!ring || !zr) return null
  const materialId = (typeof el.params.material === 'string' && el.params.material) || 'default'
  return {
    elementId: el.id,
    category: 'column',
    outlines: [ring],
    zBase: zr.z0,
    zTop: zr.z1,
    materialId,
    // same z band + material ⇒ shared chunk (grouped edges union safely — disjoint
    // profiles stay disjoint rings), keeping a column grid to a few draw calls
    groupKey: `column|${el.levelId}|${zr.z0.toFixed(4)}|${zr.z1.toFixed(4)}|${materialId}`,
    pieces: [],
    placements: [],
    openingInstances: [],
    // plan.ts's structural pass owns the plan story (cut/below/above classification)
    skipProjectedOutline: true,
    ...paintOf(el),
  }
}
